import React from "react"
import { Action, ActionStatus, UpdateActionRequest, WorkflowStepType } from "../../../models";
import { SbDialog } from "../../commons/sb-dialog";
import { t, Trans } from "@lingui/macro";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import { Editor } from "react-draft-wysiwyg";
import { convertFromRaw, convertToRaw, EditorState } from "draft-js";
import { MatesComponent, MatesComponentState } from "../../mates-component";
import withUseRouter, { UseRouterParams } from "../../../services/router";
import { WorkflowService } from "../../../services/workflow.service";
import { RestResponse } from "@sinossi/mates-react-library";
import { SbSnackbar } from "../../commons/sb-snackbar";
import { ActionsServices } from "../../../services/actions.services";
import Icon from "@mdi/react";
import { mdiEmail } from "@mdi/js";

interface ShowActionDetailsProps extends UseRouterParams {
    action: Action
    onClose: () => void
    refresh: () => void
}

interface ShowActionDetailsState extends MatesComponentState {
    editorState: EditorState
    snackbarData: {
        openNotification: boolean,
        severity: 'error' | 'info' | 'success' | 'warning',
        notificationMessage: string
    }
}

class ShowActionDetails extends React.Component<ShowActionDetailsProps, ShowActionDetailsState> {
    private readonly matesComponent: MatesComponent<ShowActionDetailsProps, ShowActionDetailsState> = new MatesComponent(this)

    constructor(props: ShowActionDetailsProps) {
        super(props)
        this.state = {
            ...this.matesComponent.setupState(),
            editorState: EditorState.createEmpty(),
            snackbarData: {
                openNotification: false,
                severity: "info",
                notificationMessage: ""
            }
        }
    }

    componentDidMount() {
        this.initEditor()
    }

    private initEditor = (): void => {
        const contentState = convertFromRaw(this.props.action.content)
        this.setState(() => ({
            editorState: EditorState.createWithContent(contentState),
        }))
    }

    private handleEditorChange = (editorState: any) => {
        this.setState(() => ({
            editorState: editorState,
        }))
    }

    private saveContent = () => {
        let request: UpdateActionRequest = {
            content: convertToRaw(this.state.editorState.getCurrentContent()),
            name: this.props.action.name,
            actionType: this.props.action.actionType,
        }

        this.setState(
            () => WorkflowService.updateActionContent(
                this.props.action.id,
                request,
                this.updateActionContentCallback,
                this.matesComponent.onException)
        )
    }

    private updateActionContentCallback = (response: RestResponse<any, any>) => {
        if (response.hasError()) {
            this.matesComponent.onException(response)
            this.handleOpenNotification(
                t({ id: "show-action-details.update-content.error", message: "Qualcosa è andato storto, riprova tra qualche istante." }),
                "error"
            )
            return
        }

        this.handleOpenNotification(
            t({ id: "show-action-details.update-content.success", message: "Modifica salvata con successo." }),
            "success"
        )

        this.props.refresh()
    }

    private handleOpenNotification = (message: string, severity: 'error' | 'info' | 'success' | 'warning') => {
        this.setState(() => ({
            snackbarData: {
                openNotification: true,
                notificationMessage: message,
                severity: severity
            }
        }))
    };

    private handleCloseNotification = () => {
        this.setState(() => ({
            snackbarData: {
                ...this.state.snackbarData,
                openNotification: false
            }
        }))
    };

    private sendEmail = () => {
        ActionsServices.sendEmail(
            this.props.action.id,
            this.sendEmailCallback,
            this.matesComponent.onException
        )
    }

    private sendEmailCallback = (response: RestResponse<any, any>) => {
        if (response.hasError()) {
            this.matesComponent.onException(response)
            this.handleOpenNotification(
                t({ id: "show-action-details.send-email.error", message: "Errore durante l'invio dell'email." }),
                "error"
            )
            return
        }

        this.handleOpenNotification(
            t({ id: "show-action-details.send-email.success", message: "Email inviata con successo." }),
            "success"
        )

        this.props.refresh()
    }

    render() {
        return (
            <>
                <SbDialog onClose={this.props.onClose}
                    width={"md"}
                    titleText={t({ id: "show-action-details.title", message: "Dettagli azione" })}
                    closeButton
                    saveButton
                    onSave={this.saveContent}
                >
                    <Typography sx={{ fontSize: 20, fontWeight: "medium" }} color="primary">
                        <Trans id={"show-action-details.action-name"}>
                            {this.props.action.name}
                        </Trans>
                    </Typography>
                    <Typography sx={{ '& > * + *': { marginLeft: '8px' }, fontWeight: "600" }}>
                        <Trans id={"show-action-details.action-type"}>
                            Tipo di azione:
                        </Trans>
                        {" "}
                        <Trans id={`enums.workflow-step-types.${this.props.action.actionType}`}>
                            
                        </Trans>
                    </Typography>
                    <Typography>
                        {
                            this.props.action.executor && this.props.action.executor.accountDetails !== null &&
                            <Trans id={"show-action-details.assigned-to"}>
                                Assegnata a: {this.props.action.executor.accountDetails.firstName + " " + this.props.action.executor.accountDetails.lastName}
                            </Trans>
                        }
                        {
                            this.props.action.executor && this.props.action.executor.accountDetails === null && this.props.action.executor.username &&
                            <Trans id={"show-action-details.assigned-to"}>
                                Assegnata a: {this.props.action.executor.username}
                            </Trans>
                        }
                        {
                            this.props.action.executor === null &&
                            <Trans id={"show-action-details.not-assigned"}>
                                Assegnata a: Nessuno
                            </Trans>
                        }
                    </Typography>
                    {
                        this.props.action.actionType === WorkflowStepType.EMAIL &&
                        <Box sx={{ mt: 2, mb: 2, textAlign: 'right' }}>
                            <Tooltip title="Invia E-Mail" placement="top" arrow>
                                <IconButton
                                    sx={{
                                        color: 'grey',
                                        backgroundColor: "transparent",
                                        ":hover": {
                                            color: "secondary.main"
                                        }
                                    }}
                                    aria-label="Send Email"
                                    onClick={this.sendEmail}
                                    disableFocusRipple
                                    disableRipple>
                                    <Icon path={mdiEmail} size={1} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    }
                    <Box sx={{ mt: 2 }}>
                        <Editor
                            readOnly={this.props.action.status !== ActionStatus.WORKING && this.props.action.status !== ActionStatus.PENDING}
                            editorState={this.state.editorState}
                            onEditorStateChange={this.handleEditorChange}
                            toolbar={{
                                options: ['inline', 'list', 'link'],
                                inline: {
                                    options: ['bold', 'italic'],
                                },

                                list: {
                                    options: ['unordered', 'ordered'],
                                },
                                link: {
                                    options: ['link'],
                                },
                            }}
                            toolbarStyle={{
                                backgroundColor: "#11347B",
                                borderColor: "#11347B"
                            }}
                            editorStyle={{
                                minHeight: "350px",
                                border: "1px solid lightgray",
                            }}
                        />
                    </Box>
                </SbDialog>
                <SbSnackbar snackbarData={this.state.snackbarData} handleCloseNotification={this.handleCloseNotification} />
            </>
        )
    }
}

export default withUseRouter(ShowActionDetails)
